import { useShopData } from '@bundles/hooks/useShopData'
import { POSTHOG_API_KEY } from '@constant/featureFlags'
import { FeatureFlagProvider } from '@fordeer/feature-flags'
import { usePosthogInit } from '@hooks/featureFlags'
import { useInitMixpanel } from '@hooks/useInitMixpanel'
import { useSupportWidget } from '@hooks/useSupportWidget'
import { warmUpChatwoot } from '@services/chatwoot'
import { usePosthogError, usePosthogReady } from '@store/posthog'
import { useContext } from '@utils/context'
import { useEffect } from 'react'
import { useShopifyToken } from '../hooks/useShopifyToken'
import { getShopifyParams } from '../libs/shopify/params'

type AppDataProviderProps = {
  children: React.ReactNode
}
const AppDataProvider: React.FC<AppDataProviderProps> = ({ children }) => {
  const { host, shop } = getShopifyParams()
  const [rootState] = useContext()
  const { hasShopData, error } = useShopData()
  const subscription = rootState?.ShopReducer?.subscription
  const shopId =
    subscription?.shop_id != null ? String(subscription.shop_id) : null
  useInitMixpanel()
  usePosthogInit({
    shopId,
    shopDomain: shop,
    planName: subscription?.type_app,
    countryCode: subscription?.country_name,
    partnerDevelopment: subscription?.partner_development,
    subscriptionCreatedAt: subscription?.created_at,
    enabled: Boolean(POSTHOG_API_KEY),
  })
  const posthogReady = usePosthogReady()
  const posthogError = usePosthogError()

  useSupportWidget({
    enabled: true,
    shopInfo: subscription ?? null,
  })

  useEffect(() => {
    console.warn('subscription', {
      subscription,
      chatwoot_base_url: import.meta.env.VITE_CHATWOOT_BASE_URL,
      chatwoot_website_token: import.meta.env.VITE_CHATWOOT_WEBSITE_TOKEN,
    })
    if (
      subscription?.chatwoot_identifier?.trim() &&
      subscription.chatwoot_identifier_hash?.trim()
    ) {
      void warmUpChatwoot()
    }
  }, [subscription])

  // App Bridge v4 is bootstrapped by the CDN script, but token loading still
  // needs to run before `RequireAuth` can succeed.
  useShopifyToken()

  useEffect(() => {
    if (host) {
      localStorage.setItem('shopify_host', host)
    }
    if (shop) {
      localStorage.setItem('shopify_shop', shop)
    }
  }, [host, shop])
  // Error loading shop data - show error state but don't block
  if (error && !hasShopData) {
    console.error('[AppData] Failed to load shop data:', error)
    // You could show a toast notification here instead of blocking
    // For now, let the app continue and individual components can handle missing shop data
  }
  return (
    <FeatureFlagProvider ready={posthogReady} error={posthogError}>
      {children}
    </FeatureFlagProvider>
  )
}
export default AppDataProvider
